// PatientVitals.js
import React, { useState } from "react";
import {
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Fab,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid2,
} from "@mui/material";
import { Add, Delete, History } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { Line } from "react-chartjs-2";
import { IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend
);

const PatientVitals = ({ currentPatient }) => {
  const [vitalsHistory, setVitalsHistory] = useState([
    {
      id: 1,
      type: "Blood Pressure",
      value: "120/80",
      recordedBy: "Dr. Smith",
      time: "2023-10-01 10:00 AM",
    },
    {
      id: 2,
      type: "Heart Rate",
      value: "72",
      recordedBy: "Dr. Smith",
      time: "2023-10-01 10:00 AM",
    },
    {
      id: 3,
      type: "Temperature",
      value: "98.6",
      recordedBy: "Dr. Smith",
      time: "2023-10-01 10:00 AM",
    },
    {
      id: 4,
      type: "Blood Pressure",
      value: "130/85",
      recordedBy: "Dr. Johnson",
      time: "2023-10-02 11:00 AM",
    },
    {
      id: 5,
      type: "Heart Rate",
      value: "75",
      recordedBy: "Dr. Johnson",
      time: "2023-10-02 11:00 AM",
    },
  ]);

  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [selectedVital, setSelectedVital] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newVital, setNewVital] = useState({
    type: "",
    value: "",
    recordedBy: "",
    time: "",
  });

  const getLatestVitals = () => {
    const latestVitals = {};
    vitalsHistory.forEach((vital) => {
      if (
        !latestVitals[vital.type] ||
        new Date(vital.time) > new Date(latestVitals[vital.type].time)
      ) {
        latestVitals[vital.type] = vital;
      }
    });
    return Object.values(latestVitals);
  };

  const handleOpenHistoryDialog = (vital) => {
    setSelectedVital(vital);
    setOpenHistoryDialog(true);
  };

  const handleCloseHistoryDialog = () => {
    setOpenHistoryDialog(false);
  };

  const handleOpenAddDialog = (vital) => {
    setSelectedVital(vital);
    setNewVital({
      type: vital.type,
      value: "",
      recordedBy: "",
      time: "",
    });
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setNewVital({ type: "", value: "", recordedBy: "", time: "" });
  };

  const handleAddVital = () => {
    const newId = vitalsHistory.length + 1;
    const newRecord = {
      id: newId,
      type: newVital.type,
      value: newVital.value,
      recordedBy: newVital.recordedBy,
      time: newVital.time || new Date().toLocaleString(),
    };

    setVitalsHistory((prev) => [...prev, newRecord]);
    handleCloseAddDialog();
  };

  const handleDeleteHistory = (id) => {
    setVitalsHistory((prev) => prev.filter((item) => item.id !== id));
  };

  const vitalsColumns = [
    { field: "type", headerName: "Vital", width: 150 },
    { field: "value", headerName: "Last Recorded Value", width: 180 },
    { field: "recordedBy", headerName: "Recorded By", width: 150 },
    { field: "time", headerName: "Time", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleOpenHistoryDialog(params.row)}>
            <History />
          </IconButton>
          <IconButton onClick={() => handleOpenAddDialog(params.row)}>
            <Add />
          </IconButton>
        </>
      ),
    },
  ];

  const filteredHistory = vitalsHistory.filter(
    (vital) => vital.type === selectedVital?.type
  );

  const chartData = {
    labels: filteredHistory.map((vital) => vital.time),
    datasets: [
      ...(selectedVital?.type === "Blood Pressure"
        ? [
            {
              label: `${selectedVital?.type} (Systolic)`,
              data: filteredHistory.map((vital) =>
                vital.type === "Blood Pressure"
                  ? vital.value.split("/")[0]
                  : null
              ),
              borderColor: "rgba(75,192,192,1)",
              fill: false,
            },
            {
              label: `${selectedVital?.type} (Diastolic)`,
              data: filteredHistory.map((vital) =>
                vital.type === "Blood Pressure"
                  ? vital.value.split("/")[1]
                  : null
              ),
              borderColor: "rgba(153,102,255,1)",
              fill: false,
            },
          ]
        : []),
      ...(selectedVital?.type === "Heart Rate"
        ? [
            {
              label: selectedVital?.type,
              data: filteredHistory.map((vital) =>
                vital.type === "Heart Rate" ? vital.value : null
              ),
              borderColor: "rgba(255,99,132,1)",
              fill: false,
            },
          ]
        : []),
      ...(selectedVital?.type === "Temperature"
        ? [
            {
              label: selectedVital?.type,
              data: filteredHistory.map((vital) =>
                vital.type === "Temperature" ? vital.value : null
              ),
              borderColor: "rgba(255,159,64,1)",
              fill: false,
            },
          ]
        : []),
    ],
  };

  return (
    <>
      <Grid2 container size={12}>
        <Grid2 size={12}>
          <Card variant="outlined" sx={{ width: "100%", height: "100%" }}>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Typography component="span">Vitals</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
                <Tooltip title="Add" arrow>
                  <Fab
                    color="secondary"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: -16,
                      right: 8,
                    }}
                  >
                    <Add sx={{ padding: "0px" }} />
                  </Fab>
                </Tooltip>

                <DataGrid
                  density="compact"
                  rows={getLatestVitals()}
                  columns={vitalsColumns}
                  pageSize={5}
                  rowsPerPageOptions={[5]}
                  disableSelectionOnClick
                  sx={{ marginTop: 2 }}
                />
              </AccordionDetails>
            </Accordion>
          </Card>
        </Grid2>
      </Grid2>

      <Dialog
        open={openHistoryDialog}
        onClose={handleCloseHistoryDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedVital?.type} History for {currentPatient.firstName}{" "}
          {currentPatient.lastName}
        </DialogTitle>
        <DialogContent>
          <Grid2 container spacing={2}>
            <Grid2 size={6}>
              <DataGrid
                density="compact"
                rows={filteredHistory}
                columns={[
                  { field: "value", headerName: "Value", width: 150 },
                  {
                    field: "recordedBy",
                    headerName: "Recorded By",
                    width: 150,
                  },
                  { field: "time", headerName: "Time", width: 200 },
                  {
                    field: "actions",
                    headerName: "Actions",
                    width: 100,
                    renderCell: (params) => (
                      <IconButton
                        onClick={() => handleDeleteHistory(params.row.id)}
                      >
                        <Delete />
                      </IconButton>
                    ),
                  },
                ]}
                pageSize={5}
                rowsPerPageOptions={[5]}
                disableSelectionOnClick
              />
            </Grid2>
            <Grid2 size={6}>
              <Line data={chartData} />
            </Grid2>
          </Grid2>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAddDialog} onClose={handleCloseAddDialog}>
        <DialogTitle>Add New Vital Record</DialogTitle>
        <DialogContent>
          <Grid2 container spacing={2} sx={{ marginTop: 1 }}>
            <Grid2 size={12}>
              <TextField
                fullWidth
                label="Type"
                value={newVital.type}
                disabled
              />
            </Grid2>
            <Grid2 size={12}>
              <TextField
                fullWidth
                label="Value"
                value={newVital.value}
                onChange={(e) =>
                  setNewVital((prev) => ({
                    ...prev,
                    value: e.target.value,
                  }))
                }
              />
            </Grid2>
            <Grid2 size={12}>
              <TextField
                fullWidth
                label="Recorded By"
                value={newVital.recordedBy}
                onChange={(e) =>
                  setNewVital((prev) => ({
                    ...prev,
                    recordedBy: e.target.value,
                  }))
                }
              />
            </Grid2>
            <Grid2 size={12}>
              <TextField
                fullWidth
                label="Time"
                value={newVital.time}
                onChange={(e) =>
                  setNewVital((prev) => ({ ...prev, time: e.target.value }))
                }
              />
            </Grid2>
          </Grid2>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button onClick={handleAddVital} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PatientVitals;